/**
 * LandFill — diagonal "tilted strips" painted over land, to read land vs ocean.
 *
 * A thin sphere just above the surface whose shader samples an equirectangular
 * land mask (white = land) and fills land with a diagonal stripe pattern; ocean
 * fragments are discarded so the dark globe shows through. The mask is sampled
 * from the *object-space* normal (matching latLonToVec3), so the fill rotates
 * with the globe and stays aligned with the coastlines and cables.
 *
 * The mask texture is built in-browser from land polygons — see
 * apps/web useLandMask and pipeline/land.py.
 */

import { useMemo } from 'react';
import { Color, FrontSide, ShaderMaterial, SphereGeometry, type Texture } from 'three';

export interface LandFillProps {
  /** Equirectangular land mask (white = land). Null until loaded. */
  readonly mask: Texture | null;
  /** Radius — just above the globe surface, below the coastline lines. */
  readonly radius?: number;
  readonly color?: string | number;
  readonly opacity?: number;
  /** Stripe density. Higher = finer strips. */
  readonly stripeFreq?: number;
}

const VERT = /* glsl */ `
  varying vec3 vLocalNormal;
  void main() {
    vLocalNormal = normal;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const FRAG = /* glsl */ `
  precision highp float;
  uniform sampler2D uMask;
  uniform vec3 uColor;
  uniform float uOpacity;
  uniform float uStripeFreq;
  varying vec3 vLocalNormal;
  #define PI 3.141592653589793

  void main() {
    vec3 n = normalize(vLocalNormal);
    float theta = atan(n.z, -n.x);          // (lon + 180) in radians
    float u = theta / (2.0 * PI);
    if (u < 0.0) u += 1.0;
    float v = acos(clamp(n.y, -1.0, 1.0)) / PI;  // 0 at north pole

    float land = texture2D(uMask, vec2(u, v)).r;
    if (land < 0.5) discard;                // ocean -> transparent

    float band = fract((u + v) * uStripeFreq);
    if (band > 0.5) discard;                // half-duty diagonal strips
    gl_FragColor = vec4(uColor, uOpacity);
  }
`;

export function LandFill({
  mask,
  radius = 1.0008,
  color = 0x1f5a78,
  opacity = 0.5,
  stripeFreq = 145.0,
}: LandFillProps): JSX.Element | null {
  const geometry = useMemo(() => new SphereGeometry(radius, 160, 80), [radius]);

  const material = useMemo(() => {
    if (!mask) return null;
    return new ShaderMaterial({
      uniforms: {
        uMask: { value: mask },
        uColor: { value: new Color(color) },
        uOpacity: { value: opacity },
        uStripeFreq: { value: stripeFreq },
      },
      vertexShader: VERT,
      fragmentShader: FRAG,
      transparent: true,
      depthWrite: false,
      side: FrontSide,
    });
  }, [mask, color, opacity, stripeFreq]);

  if (!material) return null;
  // Bottom-most transparent layer: a surface texture every marker draws over.
  // All globe layers use depthWrite:false, so their equal origin-distance makes
  // the transparent sort ambiguous; a low renderOrder pins the strips behind.
  return <mesh geometry={geometry} material={material} renderOrder={-10} />;
}
